import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Mail, KeyRound, CheckCircle2, ArrowLeft } from "lucide-react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const errToString = (detail) => {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return JSON.stringify(detail);
};

const ForgotPasswordDialog = ({ open, onOpenChange, defaultEmail = "" }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  // step: "email" -> "otp" -> "new_password" -> "done"
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState(defaultEmail || "");
  const [code, setCode] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const [lockoutUntil, setLockoutUntil] = useState(null); // ms timestamp
  const [now, setNow] = useState(Date.now());

  // Tick clock every second when locked out
  useEffect(() => {
    if (!lockoutUntil) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [lockoutUntil]);

  // Clear lockout once expired
  useEffect(() => {
    if (lockoutUntil && now >= lockoutUntil) setLockoutUntil(null);
  }, [now, lockoutUntil]);

  useEffect(() => {
    if (open) {
      setStep("email");
      setEmail(defaultEmail || "");
      setCode("");
      setNewPwd("");
      setConfirmPwd("");
    }
  }, [open, defaultEmail]);

  const locked = lockoutUntil && now < lockoutUntil;
  const lockedSeconds = locked ? Math.max(0, Math.ceil((lockoutUntil - now) / 1000)) : 0;
  const lockedMM = String(Math.floor(lockedSeconds / 60)).padStart(2, "0");
  const lockedSS = String(lockedSeconds % 60).padStart(2, "0");

  const requestOtp = async (e) => {
    e?.preventDefault?.();
    if (locked) { toast.error(`Please wait ${lockedMM}:${lockedSS} before trying again.`); return; }
    if (!email) { toast.error("Enter your email address"); return; }
    if (!executeRecaptcha) { toast.error("reCAPTCHA not ready yet. Try again."); return; }
    setLoading(true);
    try {
      const captchaToken = await executeRecaptcha("forgot_password");
      await api.post("/auth/forgot-password/request", { email, captcha_token: captchaToken });
      toast.success("Reset code sent to your email");
      setStep("otp");
    } catch (err) {
      const msg = errToString(err?.response?.data?.detail);
      toast.error(msg || "Failed to send reset code");
      if ((msg || "").toLowerCase().includes("too many")) {
        setLockoutUntil(Date.now() + 10 * 60 * 1000); // 10-minute lockout
      }
    } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    if (code.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password/verify-otp", { email, code });
      setStep("new_password");
    } catch (err) {
      const msg = errToString(err?.response?.data?.detail);
      toast.error(msg || "Invalid code");
      if (msg.toLowerCase().includes("too many")) {
        setLockoutUntil(Date.now() + 10 * 60 * 1000);
        setStep("email"); // Force restart after 3 failed attempts
      }
    } finally { setLoading(false); }
  };

  const resetPassword = async (e) => {
    e?.preventDefault?.();
    if (newPwd.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (newPwd !== confirmPwd) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password/reset", {
        email, code, new_password: newPwd, confirm_password: confirmPwd,
      });
      toast.success("Password updated successfully");
      setStep("done");
    } catch (err) {
      toast.error(errToString(err?.response?.data?.detail) || "Failed to reset password");
    } finally { setLoading(false); }
  };

  const titleByStep = {
    email: "Reset your password",
    otp: "Enter your reset code",
    new_password: "Choose a new password",
    done: "All set!",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0B1E3F]">
            {step === "email" && <Mail className="h-5 w-5" />}
            {step === "otp" && <KeyRound className="h-5 w-5" />}
            {step === "new_password" && <ShieldCheck className="h-5 w-5" />}
            {step === "done" && <CheckCircle2 className="h-5 w-5 text-green-600" />}
            {titleByStep[step]}
          </DialogTitle>
          <DialogDescription>
            {step === "email" && " "}
            {step === "otp" && " "}
            {step === "new_password" && "Choose a new password for your account."}
            {step === "done" && "Your password has been updated."}
          </DialogDescription>
        </DialogHeader>

        {step === "email" && (
          <form onSubmit={requestOtp} className="space-y-4">
            {locked && (
              <div data-testid="forgot-lockout-banner"
                className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700 flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold">Too many wrong codes</p>
                  <p className="text-xs mt-0.5">
                    For your account&apos;s safety, you can request a new code in{" "}
                    <span className="font-mono font-bold text-red-800" data-testid="forgot-lockout-countdown">{lockedMM}:{lockedSS}</span>.
                  </p>
                </div>
              </div>
            )}
            <input className="pav-input" type="email" placeholder="Your email"
              data-testid="forgot-email-input"
              value={email} onChange={(e) => setEmail(e.target.value)} required disabled={locked} />
            {/* Visible reCAPTCHA disclosure */}
            <div className="flex items-center gap-2.5 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2.5"
              data-testid="forgot-recaptcha">
              <img alt="reCAPTCHA"
                src="https://www.gstatic.com/recaptcha/api2/logo_48.png"
                className="h-7 w-7 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-semibold text-[#0B1E3F]">Protected by reCAPTCHA</p>
                <p className="text-[10px] text-neutral-500 leading-tight">
                  Google&nbsp;
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline">Privacy</a>
                  &nbsp;·&nbsp;
                  <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="underline">Terms</a>
                </p>
              </div>
              <span className="h-2 w-2 rounded-full bg-green-500" title="Active" />
            </div>
            <button type="submit" disabled={loading || locked}
              className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="forgot-request-otp-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              {locked ? `Try again in ${lockedMM}:${lockedSS}` : "Send reset code"}
            </button>
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={verifyOtp} className="space-y-4">
            <div className="rounded-xl bg-[#F4F6FB] border border-[#E2E8F0] p-3 text-sm">
              We sent a 6-digit code to <strong>{email}</strong>. Valid for 10 minutes.
            </div>
            <input type="text" maxLength={6} inputMode="numeric"
              className="pav-input tracking-[0.5em] text-center text-2xl font-mono"
              placeholder="••••••"
              data-testid="forgot-otp-input"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              autoFocus required />
            <button type="submit" disabled={loading || code.length !== 6}
              className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="forgot-verify-otp-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              Verify code
            </button>
            <button type="button" onClick={() => setStep("email")}
              className="text-xs text-neutral-500 hover:text-[#0B1E3F] inline-flex items-center gap-1"
              data-testid="forgot-back-btn">
              <ArrowLeft className="h-3 w-3" /> Use a different email
            </button>
          </form>
        )}

        {step === "new_password" && (
          <form onSubmit={resetPassword} className="space-y-3">
            <input className="pav-input" type="password" placeholder="New password (min 6 chars)"
              data-testid="forgot-new-password-input"
              value={newPwd} onChange={(e) => setNewPwd(e.target.value)} required minLength={6} />
            <input className="pav-input" type="password" placeholder="Confirm new password"
              data-testid="forgot-confirm-password-input"
              value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} required minLength={6} />
            <button type="submit" disabled={loading}
              className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="forgot-reset-password-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              Update password
            </button>
          </form>
        )}

        {step === "done" && (
          <div className="text-center space-y-3 py-4" data-testid="forgot-done">
            <div className="mx-auto h-14 w-14 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <p className="font-display text-lg font-bold text-[#0B1E3F]">Password updated</p>
            <p className="text-sm text-neutral-600">You can now sign in with your new password.</p>
            <button onClick={() => onOpenChange(false)} className="btn-primary"
              data-testid="forgot-close-btn">
              Back to sign in
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordDialog;
